import { EMPLOYEE_ROLE_LABELS } from "@etudescarre/constants";
import { Button, Drawer, Select } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { fetchAllCompanies } from "./components";

type UserInviteFormData = {
  email: string;
  firstName: string;
  lastName: string;
  companyId: number;
  role: "user" | "admin" | "root";
};

type Company = {
  id: number;
  name: string;
};

export type UserInviteFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function UserInviteForm({
  open,
  onOpenChange,
  onSuccess,
}: UserInviteFormProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<UserInviteFormData>({
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      companyId: 0,
      role: "user",
    },
  });

  const companyId = watch("companyId");
  const role = watch("role");

  // Fetch companies
  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    const { companies } = await fetchAllCompanies();
    if (isNotNil(companies)) {
      setCompanies(companies.map((c) => ({ id: c.id, name: c.name })));
    }
    setLoadingCompanies(false);
  }, []);

  // Load initial data when drawer opens
  useEffect(() => {
    if (open) {
      fetchCompanies();
      reset({
        email: "",
        firstName: "",
        lastName: "",
        companyId: 0,
        role: "user",
      });
    }
  }, [open, reset, fetchCompanies]);

  const onSubmit = async (data: UserInviteFormData) => {
    try {
      const { error } = await apiClient.POST("/api/admin/users/invite", {
        body: {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          companyId: data.companyId,
          role: data.role,
        },
      });

      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error || "Échec de l'invitation",
        });
        return;
      }

      toaster.success({
        title: "Succès",
        description:
          "Utilisateur invité. Il doit utiliser 'Mot de passe oublié' pour activer son compte.",
      });

      onSuccess?.();
      onOpenChange(false);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    }
  };

  const companyOptions = companies.map((c) => ({
    label: c.name,
    value: String(c.id),
  }));

  const roleOptions = [
    { label: EMPLOYEE_ROLE_LABELS.user, value: "user" },
    { label: EMPLOYEE_ROLE_LABELS.admin, value: "admin" },
    { label: "Root (Admin)", value: "root" },
  ];

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>Inviter un utilisateur</Drawer.Title>
                <Drawer.Description>
                  L'utilisateur devra utiliser "Mot de passe oublié" pour
                  activer son compte
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register("email")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="email@example.com"
                  />
                </div>

                {/* First Name */}
                <div>
                  <label
                    htmlFor="firstName"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    {...register("firstName")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Jean"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label
                    htmlFor="lastName"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    {...register("lastName")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Dupont"
                  />
                </div>

                {/* Company Select */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Société <span className="text-red-500">*</span>
                  </span>
                  <Select
                    options={companyOptions}
                    value={companyId ? [String(companyId)] : []}
                    onValueChange={(details) => {
                      const val = details.value[0];
                      setValue("companyId", val ? Number(val) : 0);
                    }}
                    placeholder="Sélectionner une société"
                    disabled={loadingCompanies}
                    required
                  />
                </div>

                {/* Role */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Rôle système <span className="text-red-500">*</span>
                  </span>
                  <Select
                    options={roleOptions}
                    value={[role]}
                    onValueChange={(details) => {
                      const val = details.value[0];
                      setValue(
                        "role",
                        val === "root" || val === "admin" ? val : "user",
                      );
                    }}
                    placeholder="Sélectionner un rôle"
                  />
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex shrink-0 justify-end gap-3 border-t border-zinc-200 p-6 dark:border-zinc-800">
                <Button
                  type="button"
                  outline
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "En cours..." : "Inviter"}
                </Button>
              </div>
            </form>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
